import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signInWithPopup,
    signInWithRedirect,
    GoogleAuthProvider,
    signOut as firebaseSignOut,
    sendEmailVerification,
    sendPasswordResetEmail,
    updateProfile,
    updateEmail,
    onAuthStateChanged,
    User,
    UserCredential,
} from 'firebase/auth';
import { auth } from './config';

// Google Auth Provider
const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({
    prompt: 'select_account',
});

/**
 * Sign up with email and password
 */
export const signUpWithEmail = async (
    email: string,
    password: string
): Promise<UserCredential> => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    // Send email verification
    if (userCredential.user) {
        await sendEmailVerification(userCredential.user);
    }
    return userCredential;
};

/**
 * Sign in with email and password
 */
export const signInWithEmail = async (
    email: string,
    password: string
): Promise<UserCredential> => {
    return await signInWithEmailAndPassword(auth, email, password);
};

/**
 * Sign in with Google (popup)
 */
export const signInWithGoogle = async (): Promise<UserCredential> => {
    return await signInWithPopup(auth, googleProvider);
};

/**
 * Sign in with Google (redirect) - better for mobile
 */
export const signInWithGoogleRedirect = async (): Promise<void> => {
    return await signInWithRedirect(auth, googleProvider);
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
    return await firebaseSignOut(auth);
};

/**
 * Send password reset email
 */
export const resetPassword = async (email: string): Promise<void> => {
    return await sendPasswordResetEmail(auth, email, {
        url: `${window.location.origin}/auth`,
    });
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
    user: User,
    data: { displayName?: string; photoURL?: string }
): Promise<void> => {
    return await updateProfile(user, data);
};

/**
 * Update user email
 */
export const updateUserEmail = async (user: User, newEmail: string): Promise<void> => {
    return await updateEmail(user, newEmail);
};

/**
 * Listen to auth state changes
 */
export const onAuthChange = (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback);
};

/**
 * Get current user
 */
export const getCurrentUser = (): User | null => {
    return auth.currentUser;
};
